/**
 * Public Display Board — /display/:slug
 * Industry-agnostic viewer. Real-time WebSocket updates + PWA offline cache.
 * No auth required.
 */
import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import html2canvas from 'html2canvas';
import { renderTemplate } from '../components/displayengine/TemplateFactory';

const API = process.env.REACT_APP_BACKEND_URL;
const WS_BASE = API.replace(/^http/, 'ws');
const CACHE_KEY = (slug) => `displayengine:cache:${slug}`;

export default function DisplayBoard() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [wsConnected, setWsConnected] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const wsRef = useRef(null);
  const reconnectRef = useRef(null);
  const boardRef = useRef(null);

  // Load board data (network-first, fallback to cache)
  const load = async (showSpinner = true) => {
    try {
      const res = await axios.get(`${API}/api/displayengine/by-slug/${slug}`);
      setData(res.data);
      try { localStorage.setItem(CACHE_KEY(slug), JSON.stringify({ payload: res.data, ts: Date.now() })); } catch {}
      setError(null);
    } catch (e) {
      // Fallback to cached payload
      try {
        const cached = localStorage.getItem(CACHE_KEY(slug));
        if (cached) {
          const parsed = JSON.parse(cached);
          setData(parsed.payload);
          setError('offline-cache');
        } else {
          setError(e.response?.data?.detail || 'Failed to load board');
        }
      } catch {
        setError(e.message || 'Failed');
      }
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [slug]);

  // Online / offline events
  useEffect(() => {
    const goOnline = () => { setIsOffline(false); load(false); };
    const goOffline = () => setIsOffline(true);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
    // eslint-disable-next-line
  }, [slug]);

  // WebSocket realtime
  useEffect(() => {
    if (!data?.client?.id) return;
    let cancelled = false;
    const connect = () => {
      if (cancelled) return;
      try {
        const ws = new WebSocket(`${WS_BASE}/api/displayengine/ws/${data.client.id}`);
        wsRef.current = ws;
        ws.onopen = () => setWsConnected(true);
        ws.onmessage = (ev) => {
          try {
            const msg = JSON.parse(ev.data);
            if (['client_updated', 'theme_changed', 'items_changed', 'schema_changed'].includes(msg.type)) {
              load(false);
            }
          } catch {}
        };
        ws.onclose = () => {
          setWsConnected(false);
          if (!cancelled) {
            reconnectRef.current = setTimeout(connect, 3000);
          }
        };
        ws.onerror = () => { try { ws.close(); } catch {} };
      } catch {
        if (!cancelled) reconnectRef.current = setTimeout(connect, 3000);
      }
    };
    connect();
    return () => {
      cancelled = true;
      if (reconnectRef.current) clearTimeout(reconnectRef.current);
      try { wsRef.current?.close(); } catch {}
    };
  }, [data?.client?.id]);

  // Apply CSS variables from theme to a wrapper
  if (error && !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900 text-white p-8 text-center" data-testid="board-error">
        <div>
          <h1 className="text-4xl font-bold mb-3">Display Board Unavailable</h1>
          <p className="text-slate-400">{typeof error === 'string' && error !== 'offline-cache' ? error : 'No internet & no cached snapshot found.'}</p>
        </div>
      </div>
    );
  }
  if (!data) {
    return <div className="min-h-screen flex items-center justify-center bg-slate-900 text-white" data-testid="board-loading">Loading…</div>;
  }

  const { client, theme, fields, items } = data;
  const cssVars = {
    backgroundColor: theme.background_color,
    color: theme.text_color,
    fontFamily: theme.font_family || 'Inter, sans-serif',
    minHeight: '100vh',
  };

  const downloadImage = async (format = 'jpeg') => {
    if (!boardRef.current) return;
    setDownloading(true);
    try {
      const canvas = await html2canvas(boardRef.current, {
        backgroundColor: format === 'png' ? null : (theme.background_color || '#000'),
        scale: 2,
        useCORS: true,
        allowTaint: true,
        logging: false,
      });
      const mime = format === 'png' ? 'image/png' : 'image/jpeg';
      const ext = format === 'png' ? 'png' : 'jpg';
      const dataUrl = canvas.toDataURL(mime, 0.92);
      const link = document.createElement('a');
      const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      link.download = `${client.slug || 'display-board'}-${stamp}.${ext}`;
      link.href = dataUrl;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error(`${format.toUpperCase()} export failed:`, err);
      alert(`${format.toUpperCase()} download failed: ` + (err.message || 'unknown error'));
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div style={cssVars} data-testid={`board-${client.slug}`} ref={boardRef}>
      {/* Header / Logo */}
      <header className="flex items-center justify-between p-4 md:p-6 border-b" style={{ borderColor: theme.primary_color + '30' }}>
        <div className="flex items-center gap-3">
          {theme.show_logo && theme.logo_url && (
            <img src={theme.logo_url} alt={client.name} className="h-10 md:h-12 object-contain" />
          )}
          <div>
            <h1 className="text-xl md:text-2xl font-extrabold" style={{ color: theme.primary_color, fontFamily: theme.heading_font || theme.font_family }} data-testid="board-client-name">{client.name}</h1>
            {client.description && <p className="text-xs md:text-sm opacity-60" data-testid="board-client-desc">{client.description}</p>}
          </div>
        </div>
        <div className="flex items-center gap-2 text-xs">
          {isOffline && <span className="px-2 py-1 rounded-full bg-amber-500/20 text-amber-300" data-testid="board-offline">📴 Offline</span>}
          {!isOffline && (
            <span className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-emerald-500/20 text-emerald-300" data-testid="board-online">
              <span className={`w-1.5 h-1.5 rounded-full ${wsConnected ? 'bg-emerald-400 animate-pulse' : 'bg-amber-400'}`} />
              {wsConnected ? 'LIVE' : 'Connecting…'}
            </span>
          )}
          {error === 'offline-cache' && <span className="px-2 py-1 rounded-full bg-blue-500/20 text-blue-300">From Cache</span>}
          <button
            onClick={() => downloadImage('jpeg')}
            disabled={downloading}
            className="px-3 py-1 rounded-full bg-amber-500/30 hover:bg-amber-500/50 text-amber-100 text-xs font-bold transition disabled:opacity-50 print:hidden"
            data-testid="board-download-jpeg"
            title="Download board as JPEG"
          >
            {downloading ? '⏳ …' : '⬇ JPG'}
          </button>
          <button
            onClick={() => downloadImage('png')}
            disabled={downloading}
            className="px-3 py-1 rounded-full bg-blue-500/30 hover:bg-blue-500/50 text-blue-100 text-xs font-bold transition disabled:opacity-50 print:hidden"
            data-testid="board-download-png"
            title="Download board as PNG (transparent bg)"
          >
            {downloading ? '⏳ …' : '⬇ PNG'}
          </button>
        </div>
      </header>

      {/* Main board */}
      <main>
        {renderTemplate({ client, theme, fields, items })}
      </main>

      {/* Ticker */}
      {theme.show_ticker && theme.ticker_text && (
        <div className="fixed bottom-0 left-0 right-0 overflow-hidden border-t py-2 backdrop-blur"
             style={{ backgroundColor: theme.secondary_color + 'ee', borderColor: theme.primary_color + '40' }} data-testid="board-ticker">
          <div className="whitespace-nowrap animate-marquee text-base md:text-xl font-semibold" style={{ color: theme.accent_color, fontFamily: theme.heading_font || theme.font_family }}>
            {theme.ticker_text + '   ◆   ' + theme.ticker_text}
          </div>
        </div>
      )}
      <style>{`
        @keyframes marquee {
          0% { transform: translateX(0%); }
          100% { transform: translateX(-50%); }
        }
        .animate-marquee {
          animation: marquee 35s linear infinite;
          display: inline-block;
          padding-left: 100%;
        }
      `}</style>
    </div>
  );
}
